import React from "react";
import { useParams } from "react-router-dom";
import { Loader, Modal, Textarea, Group, Button } from "@mantine/core";
import { useNavigate } from "react-router";
import api from "../../../services/ApiService";
import ApartmentView from "./ApartmentView.jsx";
import { useTrans } from "core/index.js";
import { notifications } from "@mantine/notifications";
import { Api_Apartment } from "../../../../../constants/SubApi.js";

export default function ApartmentDetails({ type }) {
	const { t } = useTrans();
	const { id } = useParams();
	const navigate = useNavigate();

	const [loading, setLoading] = React.useState(true);
	const [apt, setApt] = React.useState(null);
	const [showPublishConfirm, setShowPublishConfirm] = React.useState(false);
	const [showRefuseDialog, setShowRefuseDialog] = React.useState(false);
	const [refuseReason, setRefuseReason] = React.useState("");
	const [saving, setSaving] = React.useState(false);
	const [hasError, setHasError] = React.useState(false);

	const excuteAction = () => {
		if (type === "book") {
			navigate(`/apartment/reserve/${id}`);
		} else if (type === "publish") {
			setShowPublishConfirm(true);
		}
	};

	const excuteClose = () => {
		if (type === "book") {
			navigate(-1);
		} else if (type === "publish") {
			setShowRefuseDialog(true);
		}
	};

	const doPublish = async () => {
		if (!apt) return;
		setSaving(true);
		try {
			await api.patch(`/${Api_Apartment}`, { id: apt.id, status: 1 });
			notifications.show({ color: "green", title: "Success", message: "Apartment published" });
			setShowPublishConfirm(false);

			navigate(-1);
		} catch (err) {
			notifications.show({ color: "red", title: "Error", message: err?.message || "Publish failed" });
			console.error("publish error", err);
		} finally {
			setSaving(false);
		}
	};

	const doRefuse = async () => {
		if (!apt) return;
		const reason = (refuseReason || "").trim();
		if (!reason) {
			notifications.show({ color: "red", title: "Error", message: "Please enter a reason" });
			return;
		}
		setSaving(true);
		try {
			await api.patch(`/${Api_Apartment}`, { id: apt.id, status: 2, requirements: reason });
			notifications.show({ color: "green", title: "Success", message: "Apartment refused" });
			setShowRefuseDialog(false);
			navigate(-1);
		} catch (err) {
			notifications.show({ color: "red", title: "Error", message: err?.message || "Refuse failed" });
			console.error("refuse error", err);
		} finally {
			setSaving(false);
		}
	};

	React.useEffect(() => {
		let mounted = true;

		async function load() {
			setLoading(true);
			setHasError(false);
			try {
				const res = await api.get(`/${Api_Apartment}/${id}`);
				if (mounted) {
					if (res) {
						setApt(res);
					} else {
						setHasError(true);
					}
				}
			} catch (err) {
				console.error("Failed to load apartment", err);
				if (mounted) {
					setHasError(true);
				}
			} finally {
				if (mounted) setLoading(false);
			}
		}

		load();
		return () => (mounted = false);
	}, [id]);

	if (loading)
		return (
			<div style={{ padding: 32, textAlign: "center" }}>
				<Loader size="xl" color="blue" />
			</div>
		);

	if (!apt || hasError)
		return (
			<div style={{ padding: 32, textAlign: "center" }}>{hasError && t("messages.errors.apartment.NotFound")}</div>
		);

	return (
		<div style={{ padding: 20, maxWidth: 1000, margin: "0 auto" }}>
			{/* Publish confirm modal */}
			<Modal
				opened={showPublishConfirm}
				onClose={() => setShowPublishConfirm(false)}
				title={t("baseApartmentDetails.publishedApartment")}
			>
				<div style={{ marginBottom: 12 }}>{t("messages.confirmPublishedApartment")}</div>
				<Group position="right">
					<Button variant="default" onClick={() => setShowPublishConfirm(false)} disabled={saving}>
						{t("baseApartmentDetails.cancel")}
					</Button>
					<Button color="primary" onClick={doPublish} loading={saving}>
						{t("baseApartmentDetails.publish")}
					</Button>
				</Group>
			</Modal>

			{/* Refuse dialog */}
			<Modal
				opened={showRefuseDialog}
				onClose={() => setShowRefuseDialog(false)}
				title={t("baseApartmentDetails.refuseApartment")}
				size="lg"
			>
				<Textarea
					placeholder={t("baseApartmentDetails.reasonRefuse")}
					value={refuseReason}
					onChange={(e) => setRefuseReason(e.target.value)}
					minRows={4}
				/>
				<Group position="right" style={{ marginTop: 12 }}>
					<Button variant="default" onClick={() => setShowRefuseDialog(false)} disabled={saving}>
						{t("apartment.cancel")}
					</Button>
					<Button color="red" onClick={doRefuse} loading={saving}>
						{t("baseApartmentDetails.submit")}
					</Button>
				</Group>
			</Modal>
			<ApartmentView
				type={type}
				apt={apt}
				buttonLabel={t(`baseApartmentDetails.${type}`)}
				closeLabel={type === "book" ? t(`baseApartmentDetails.cancel`) : t(`baseApartmentDetails.refuse`)}
				onAction={() => excuteAction()}
				onCancel={() => excuteClose()}
			/>
		</div>
	);
}
